import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import routeTrackApi from "../services/routeTrackApi";
import targetsApi from "../services/targetsApi";
import { getAllVillageAction } from "./Master/Village/getEditeVillage";
import DataGridTable from "./table/DataGridTable";
import { buildRouteDetailReportPlainText } from "../utils/routeTrackReportText";
import {
  formatDateTimeIST,
  formatRouteStatusLabel,
} from "../utils/routeTrackFormat";

/**
 * Very lightweight daily route planner + tracker for web.
 * - Pick person + date.
 * - Define ordered stops for the day.
 * - Field staff can tick "visited" when they reach a stop.
 *
 * This is intentionally simple and self‑contained so it can be
 * linked from existing menus without touching Targets.jsx.
 */

function routeDisplayStatus(routeDateStr, routeStatusRaw) {
  const raw = routeStatusRaw || "";
  const rd = dayjs(String(routeDateStr || "").slice(0, 10));
  if (!rd.isValid()) return raw;
  if (rd.isBefore(dayjs(), "day") && raw !== "completed") return "expired";
  return raw;
}

export default function RouteTrack() {
  const today = useMemo(() => dayjs().format("YYYY-MM-DD"), []);
  const [date] = useState(today);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const rangeStartDate = useMemo(
    () => (startDate ? new Date(startDate) : null),
    [startDate]
  );
  const rangeEndDate = useMemo(
    () => (endDate ? new Date(endDate) : null),
    [endDate]
  );
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState("all");

  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [, setLoadingRoute] = useState(false);
  const [savingRoute, setSavingRoute] = useState(false);
  const [loadingVillages, setLoadingVillages] = useState(false);

  const [route, setRoute] = useState(null); // last saved / loaded route
  const [, setEditStops] = useState([
    { name: "", address: "", villageId: null },
  ]);
  const [error, setError] = useState("");
  const [assignedVillages, setAssignedVillages] = useState([]);
  const [gridRows, setGridRows] = useState([]);
  const [gridLoading, setGridLoading] = useState(false);
  const [plannerOpen, setPlannerOpen] = useState(false);
  const [dailyReport, setDailyReport] = useState(null);
  const [loadingDailyReport, setLoadingDailyReport] = useState(false);
  const [csvExportBusy, setCsvExportBusy] = useState(false);

  const [plannerEmployeeId, setPlannerEmployeeId] = useState("me");
  const [plannerDate, setPlannerDate] = useState(today);
  const [, setPlannerRoute] = useState(null);
  const [plannerEditStops, setPlannerEditStops] = useState([
    { name: "", address: "", villageId: null },
  ]);
  const [plannerRepeatWeekly, setPlannerRepeatWeekly] = useState(false);
  const [plannerRepeatUntil, setPlannerRepeatUntil] = useState("");

  const plannerSelectedWeekday = useMemo(
    () => dayjs(plannerDate).format("dddd"),
    [plannerDate]
  );
  const plannerDateIsPast = useMemo(
    () => dayjs(String(plannerDate).slice(0, 10)).isBefore(dayjs(), "day"),
    [plannerDate]
  );

  const DateRangeField = React.forwardRef(function DateRangeField(
    { value, onClick, onChange, placeholder },
    ref
  ) {
    return (
      <TextField
        size="small"
        label="Date range"
        value={value}
        onClick={onClick}
        onChange={onChange}
        inputRef={ref}
        placeholder={placeholder}
        sx={{ minWidth: 300 }}
        InputProps={{ readOnly: true }}
      />
    );
  });

  const [villageFilterId, setVillageFilterId] = useState("");


  useEffect(() => {
    const loadEmployees = async () => {
      setLoadingEmployees(true);
      try {
        const res = await targetsApi.getEmployeesForTargets();
        if (res?.isSuccess && Array.isArray(res?.result)) {
          setEmployees(res.result);
        } else {
          setEmployees([]);
        }
      } catch {
        setEmployees([]);
      } finally {
        setLoadingEmployees(false);
      }
    };
    loadEmployees();
  }, []);

  const isViewAll = employeeId === "all";

  const resolvedEmployeeId = useMemo(() => {
    if (employeeId === "all" || employeeId === "me" || !employeeId) return null;
    const v = parseInt(employeeId, 10);
    return Number.isNaN(v) ? null : v;
  }, [employeeId]);

  const resolvedPlannerEmployeeId = useMemo(() => {
    if (
      plannerEmployeeId === "all" ||
      plannerEmployeeId === "me" ||
      !plannerEmployeeId
    )
      return null;
    const v = parseInt(plannerEmployeeId, 10);
    return Number.isNaN(v) ? null : v;
  }, [plannerEmployeeId]);

  const loadAllVillages = async () => {
    setAssignedVillages([]);
    setLoadingVillages(true);
    try {
      const res = await getAllVillageAction();
      const rows = Array.isArray(res?.result) ? res.result : [];
      if (rows.length) {
        const villageMap = new Map();
        rows.forEach((row) => {
          const id = row.id ?? row.village_id;
          const name = row.name ?? row.village_name;
          if (id == null || !name || villageMap.has(id)) return;
          villageMap.set(id, {
            id,
            name,
            latitude: row.latitude,
            longitude: row.longitude,
          });
        });
        const villages = Array.from(villageMap.values());
        setAssignedVillages(villages);
      } else {
        setAssignedVillages([]);
      }
    } catch {
      setAssignedVillages([]);
    } finally {
      setLoadingVillages(false);
    }
  };

  const firstEmployeeOption = useMemo(() => {
    if (employees.length) {
      const first = employees[0];
      return String(first.user_id ?? first.id ?? "me");
    }
    return "me";
  }, [employees]);

  const openCreateRouteDialog = () => {
    setError("");
    setPlannerRoute(null);
    setPlannerEditStops([{ name: "", address: "", villageId: null }]);
    setPlannerRepeatWeekly(false);
    setPlannerRepeatUntil("");
    setPlannerDate(today);
    setPlannerEmployeeId(firstEmployeeOption);
    setPlannerOpen(true);
  };

  const openPlannerExistingRoute = useCallback(async (row) => {
    setError("");
    setPlannerEmployeeId(String(row.employee_id ?? "me"));
    setPlannerDate(dayjs(row.date).format("YYYY-MM-DD"));
    setPlannerOpen(true);
    await loadPlannerRoute(
      dayjs(row.date).format("YYYY-MM-DD"),
      Number(row.employee_id)
    );
  }, []);

  const loadRoute = async () => {
    setLoadingRoute(true);
    setError("");
    try {
      const params = { date };
      if (resolvedEmployeeId != null) params.employeeId = resolvedEmployeeId;
      const res = await routeTrackApi.getRouteForDay(params);
      if (res?.isSuccess) {
        setRoute(res.result || null);
        if (res.result?.stops?.length) {
          const mapped = res.result.stops.map((s) => ({
            id: s.id,
            villageId: s.village_id ?? null,
            name: s.name || "",
            address: s.address || "",
            status: s.status,
            visitedAt: s.actual_arrival_time || "",
          }));
          setEditStops(mapped);
        } else {
          setEditStops([{ name: "", address: "", villageId: null }]);
        }
      } else {
        setRoute(null);
        setEditStops([{ name: "", address: "", villageId: null }]);
        setError(res?.result || "Failed to load route");
      }
    } catch (e) {
      setRoute(null);
      setEditStops([{ name: "", address: "", villageId: null }]);
      setError(
        e?.response?.data?.result || e?.message || "Failed to load route"
      );
    } finally {
      setLoadingRoute(false);
    }
  };

  const loadPlannerRoute = async (targetDate, targetEmployeeId) => {
    setLoadingRoute(true);
    setError("");
    try {
      const params = { date: targetDate };
      if (targetEmployeeId != null) params.employeeId = targetEmployeeId;
      const res = await routeTrackApi.getRouteForDay(params);
      if (res?.isSuccess) {
        setPlannerRoute(res.result || null);
        setPlannerRepeatWeekly(Boolean(res.result?.repeat_weekly));
        setPlannerRepeatUntil(
          res.result?.repeat_until_date
            ? String(res.result.repeat_until_date).slice(0, 10)
            : ""
        );
        if (res.result?.stops?.length) {
          const mapped = res.result.stops.map((s) => ({
            id: s.id,
            villageId: s.village_id ?? null,
            name: s.name || "",
            address: s.address || "",
            status: s.status,
            visitedAt: s.actual_arrival_time || "",
          }));
          setPlannerEditStops(mapped);
        } else {
          setPlannerEditStops([{ name: "", address: "", villageId: null }]);
        }
      } else {
        setPlannerRoute(null);
        setPlannerRepeatWeekly(false);
        setPlannerRepeatUntil("");
        setPlannerEditStops([{ name: "", address: "", villageId: null }]);
        setError(res?.result || "Failed to load route");
      }
    } catch (e) {
      setPlannerRoute(null);
      setPlannerRepeatWeekly(false);
      setPlannerRepeatUntil("");
      setPlannerEditStops([{ name: "", address: "", villageId: null }]);
      setError(
        e?.response?.data?.result || e?.message || "Failed to load route"
      );
    } finally {
      setLoadingRoute(false);
    }
  };

  useEffect(() => {
    loadAllVillages();
    if (date) {
      loadRoute();
    } else {
      setRoute(null);
      setEditStops([{ name: "", address: "", villageId: null }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, resolvedEmployeeId]);

  const routeStopSig = useMemo(
    () =>
      !route?.stops?.length
        ? ""
        : route.stops
            .map(
              (s) =>
                `${s.id}:${s.status}:${s.actual_arrival_time || ""}`
            )
            .join("|"),
    [route?.stops]
  );

  useEffect(() => {
    let cancelled = false;
    if (!route?.id) {
      setDailyReport(null);
      setLoadingDailyReport(false);
      return undefined;
    }
    setLoadingDailyReport(true);
    const params = { date };
    if (resolvedEmployeeId != null) params.employeeId = resolvedEmployeeId;
    routeTrackApi
      .getDailyReport(params)
      .then((res) => {
        if (cancelled) return;
        if (res?.isSuccess) setDailyReport(res.result);
        else setDailyReport(null);
      })
      .catch(() => {
        if (!cancelled) setDailyReport(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingDailyReport(false);
      });
    return () => {
      cancelled = true;
    };
  }, [route?.id, routeStopSig, date, resolvedEmployeeId]);

  const loadGrid = async (override = {}) => {
    setGridLoading(true);
    setError("");
    try {
      const params = {};
      const rangeStart = override.startDate ?? startDate;
      const rangeEnd = override.endDate ?? endDate;
      if (rangeStart && rangeEnd) {
        params.startDate = rangeStart;
        params.endDate = rangeEnd;
      } else if (override.date) {
        params.date = override.date;
      }
      if (isViewAll) params.viewAll = "1";
      else if (resolvedEmployeeId != null) params.employeeId = resolvedEmployeeId;
      if (villageFilterId) params.villageId = villageFilterId;
      
      const res = await routeTrackApi.getRoutesSummary(params);
      
      if (res?.isSuccess && Array.isArray(res.result)) {
        let indexID = 0;
        const mapped = res.result.map((r) => {
          const dateStr = String(r.route_date || "").slice(0, 10);
          const rawStatus = r.route_status || "";
          const row = {
            indexID: ++indexID,
            route_id: r.route_id,
            employee_id: r.employee_id,
            employee_name:
              (r.first_name || "") + " " + (r.last_name || "") || r.employee_id,
            date: dateStr,
            status: formatRouteStatusLabel(
              routeDisplayStatus(dateStr, rawStatus)
            ),
            total_stops: r.total_stops ?? 0,
            visited_stops: r.visited_stops ?? 0,
            pending_stops: r.pending_stops ?? 0,
            skipped_stops: r.skipped_stops ?? 0,
            last_visited_at:
              formatDateTimeIST(r.last_visited_at) ||
              (r.last_visited_at ? String(r.last_visited_at) : ""),
          };
          return row;
        });
        setGridRows(mapped);
      } else {
        setGridRows([]);
        if (!res?.isSuccess) {
          setError(res?.result || "Failed to load routes");
        }
      }
    } catch (e) {
      setGridRows([]);
      setError(e?.message || "Error loading routes");
    } finally {
      setGridLoading(false);
    }
  };

  useEffect(() => {
    loadGrid();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, resolvedEmployeeId, villageFilterId, isViewAll]);

  const handlePlannerStopChange = (idx, field, value) => {
    setPlannerEditStops((prev) =>
      prev.map((s, i) => (i === idx ? { ...s, [field]: value } : s))
    );
  };

  const onPlannerStopVillageSelected = async (idx, rawVillageId) => {
    const vid =
      rawVillageId != null && rawVillageId !== ""
        ? parseInt(String(rawVillageId), 10)
        : null;
    handlePlannerStopChange(idx, "villageId", rawVillageId || null);
    if (!vid || Number.isNaN(vid)) return;
    const v = assignedVillages.find((x) => Number(x.id) === vid);
    const hasCoords =
      v &&
      v.latitude != null &&
      v.longitude != null &&
      String(v.latitude).trim() !== "" &&
      String(v.longitude).trim() !== "";
    if (hasCoords) return;
    try {
      const res = await routeTrackApi.ensureVillageGeo({ villageId: vid });
      if (res?.isSuccess && res?.result) {
        setAssignedVillages((prev) =>
          prev.map((x) =>
            Number(x.id) === vid
              ? {
                  ...x,
                  latitude: res.result.lat,
                  longitude: res.result.lng,
                }
              : x
          )
        );
      }
    } catch {
      /* geocode is optional; save route still attempts server-side fill */
    }
  };

  const addPlannerEmptyStopRow = () => {
    setPlannerEditStops((prev) => [
      ...prev,
      { name: "", address: "", villageId: null },
    ]);
  };

  const saveRouteForDay = async () => {
    if (plannerDateIsPast) {
      setError(
        "This date is in the past. Routes cannot be created or edited."
      );
      return;
    }
    const cleaned = plannerEditStops
      .map((s, index) => {
        const villageId =
          s.villageId != null ? parseInt(s.villageId, 10) || null : null;
        const fromList = assignedVillages.find(
          (v) => Number(v.id) === Number(villageId)
        );
        const name = fromList ? fromList.name : (s.name || "").trim();
        const latRaw =
          fromList?.latitude != null && fromList.latitude !== ""
            ? parseFloat(fromList.latitude)
            : NaN;
        const lngRaw =
          fromList?.longitude != null && fromList.longitude !== ""
            ? parseFloat(fromList.longitude)
            : NaN;
        const out = {
          villageId,
          name,
          address: (s.address || "").trim() || undefined,
          sequence_no: index + 1,
        };
        if (Number.isFinite(latRaw)) out.latitude = latRaw;
        if (Number.isFinite(lngRaw)) out.longitude = lngRaw;
        return out;
      })
      .filter((s) => s.villageId || s.name);
    if (!cleaned.length) {
      setError("Add at least one stop name.");
      return;
    }
    setSavingRoute(true);
    setError("");
    try {
      const payload = {
        date: plannerDate,
        stops: cleaned,
        repeatWeekly: plannerRepeatWeekly,
      };
      if (plannerRepeatWeekly && plannerRepeatUntil)
        payload.repeatUntil = plannerRepeatUntil;
      if (resolvedPlannerEmployeeId != null)
        payload.employeeId = resolvedPlannerEmployeeId;
      const res = await routeTrackApi.saveRoute(payload);
      if (!res?.isSuccess) {
        setError(res?.result || "Failed to save route");
        return;
      }
      setPlannerRoute(res.result || null);
      if (res.result?.stops?.length) {
        const mapped = res.result.stops.map((s) => ({
          id: s.id,
          villageId: s.village_id ?? null,
          name: s.name || "",
          address: s.address || "",
          status: s.status,
          visitedAt: s.actual_arrival_time || "",
        }));
        setPlannerEditStops(mapped);
      }
      const y = parseInt(String(plannerDate).slice(0, 4), 10);
      const m = parseInt(String(plannerDate).slice(5, 7), 10);
      if (Number.isFinite(y) && Number.isFinite(m)) {
        await loadGrid();
      } else {
        await loadGrid();
      }
    } catch (e) {
      setError(
        e?.response?.data?.result || e?.message || "Failed to save route"
      );
    } finally {
      setSavingRoute(false);
    }
  };

  const deleteRouteForDay = useCallback(async (row) => {
    if (dayjs(String(row.date).slice(0, 10)).isBefore(dayjs(), "day")) {
      setError("Past routes cannot be deleted.");
      return;
    }
    const ok = window.confirm(
      `Delete route for ${row.employee_name} on ${row.date}?`
    );
    if (!ok) return;
    setError("");
    try {
      await routeTrackApi.deleteRoute({
        date: row.date,
        employeeId: row.employee_id,
      });
      await loadGrid();
      // If currently opened route matches deleted date, clear it.
      if (String(date) === String(row.date)) {
        setRoute(null);
        setEditStops([{ name: "", address: "", villageId: null }]);
      }
    } catch (e) {
      setError(
        e?.response?.data?.result || e?.message || "Failed to delete route"
      );
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const gridColumns = useMemo(
    () => [
      { field: "employee_name", headerName: "Employee", minWidth: 180, flex: 1 },
      { field: "date", headerName: "Date", minWidth: 120, flex: 1 },
      { field: "total_stops", headerName: "Stops", minWidth: 90, flex: 0.5 },
      {
        field: "visited_stops",
        headerName: "Visited",
        minWidth: 90,
        flex: 0.5,
      },
      {
        field: "pending_stops",
        headerName: "Pending",
        minWidth: 90,
        flex: 0.5,
      },
      { field: "status", headerName: "Status", minWidth: 100, flex: 0.7 },
      {
        field: "last_visited_at",
        headerName: "Last Visit",
        minWidth: 160,
        flex: 1,
      },
      {
        field: "action",
        headerName: "Action",
        minWidth: 200,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <div style={{ display: "flex", gap: 8 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => openPlannerExistingRoute(params.row)}
            >
              View
            </Button>
            <Button
              size="small"
              color="error"
              variant="outlined"
              onClick={() => deleteRouteForDay(params.row)}
              disabled={dayjs(String(params.row.date).slice(0, 10)).isBefore(
                dayjs(),
                "day"
              )}
            >
              Delete
            </Button>
          </div>
        ),
      },
    ],
    [openPlannerExistingRoute, deleteRouteForDay]
  );

  const downloadVisitReportCsv = async () => {
    const esc = (v) => {
      const s = v == null ? "" : String(v);
      if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const stopStatusLabel = (s) => {
      if (s === "visited") return "Visited";
      if (s === "skipped") return "Skipped";
      return "Pending";
    };
    const formatVisitedAt = (raw) => formatDateTimeIST(raw) || String(raw || "");

    setCsvExportBusy(true);
    setError("");
    try {
      const params = {};
      if (startDate && endDate) {
        params.startDate = startDate;
        params.endDate = endDate;
      }
      if (isViewAll) params.viewAll = "1";
      else if (resolvedEmployeeId != null) params.employeeId = resolvedEmployeeId;
      if (villageFilterId) params.villageId = villageFilterId;
      const res = await routeTrackApi.getRoutesGrid(params);
      const stopRows = Array.isArray(res?.result) ? res.result : [];
      if (!stopRows.length) {
        setError("No route stops for the current filters.");
        return;
      }

      // Group stops by employee+date
      const stopsByRoute = new Map();
      stopRows.forEach((r) => {
        const rd = String(r.route_date || "").slice(0, 10);
        const k = `${rd}|${String(r.employee_id)}`;
        if (!stopsByRoute.has(k)) stopsByRoute.set(k, []);
        stopsByRoute.get(k).push(r);
      });

      const summaryFromStops = (routeDateStr, stopsInRoute) => {
        if (!stopsInRoute?.length) return null;
        const total = stopsInRoute.length;
        const visited = stopsInRoute.filter((s) => s.status === "visited").length;
        const pending = stopsInRoute.filter((s) => s.status === "pending").length;
        const skipped = stopsInRoute.filter((s) => s.status === "skipped").length;
        const allDone = stopsInRoute.every(
          (s) => s.status === "visited" || s.status === "skipped"
        );
        const anyVisited = stopsInRoute.some((s) => s.status === "visited");
        let raw = "planned";
        if (allDone) raw = "completed";
        else if (anyVisited) raw = "in_progress";
        return {
          total_stops: total,
          visited_stops: visited,
          pending_stops: pending,
          skipped_stops: skipped,
          status: formatRouteStatusLabel(routeDisplayStatus(routeDateStr, raw)),
        };
      };

      const headers = [
        "Employee",
        "Date",
        "Village name",
        "Stop status",
        "Visited at",
      ];
      const lines = [headers.join(",")];

      // Sort routes by date (descending) and employee name
      const sortedRoutes = Array.from(stopsByRoute.entries()).sort((a, b) => {
        const [keyA] = a;
        const [keyB] = b;
        const [dateA] = keyA.split("|");
        const [dateB] = keyB.split("|");
        if (dateA !== dateB) return dateB.localeCompare(dateA); // descending by date
        return keyA.localeCompare(keyB);
      });

      sortedRoutes.forEach(([key, stops]) => {
        if (!stops?.length) return;
        const [routeDate] = key.split("|");
        const empName =
          `${stops[0].first_name || ""} ${stops[0].last_name || ""}`.trim() ||
          String(stops[0].employee_id ?? "");

        stops.forEach((r, idx) => {
          const village =
            String(r.village_name || r.stop_name || "").trim() || "-";
          lines.push(
            [
              esc(idx === 0 ? empName : ""),
              esc(idx === 0 ? routeDate : ""),
              esc(village),
              esc(stopStatusLabel(r.status)),
              esc(formatVisitedAt(r.actual_arrival_time)),
            ].join(",")
          );
        });

        const summary = summaryFromStops(routeDate, stops);
        const total = Number(summary?.total_stops) || 0;
        const visited = Number(summary?.visited_stops) || 0;
        const pending = Number(summary?.pending_stops) || 0;
        const pct = total > 0 ? Math.round((visited / total) * 100) : "";

        lines.push("");
        lines.push(
          [
            "Route stops",
            esc(total),
            "Route visited",
            esc(visited),
            "Route pending",
            esc(pending),
            "Visit %",
            esc(pct === "" ? "" : `${pct}%`),
          ].join(",")
        );
        lines.push("");
      });

      const bom = "\uFEFF";
      const blob = new Blob([bom + lines.join("\r\n")], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const dateStr = startDate && endDate ? `${startDate}-to-${endDate}` : "routes";
      a.download = `route-visit-detail-${dateStr}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(
        e?.response?.data?.result ||
          e?.message ||
          "Failed to download CSV report."
      );
    } finally {
      setCsvExportBusy(false);
    }
  };

  const shareDetailReport = async () => {
    if (!dailyReport) return;
    const text = buildRouteDetailReportPlainText(dailyReport);
    try {
      if (typeof navigator !== "undefined" && navigator.share) {
        await navigator.share({ title: "Route detail report", text });
        return;
      }
      if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        window.alert("Copied to clipboard.");
        return;
      }
      window.prompt("Copy this report (Ctrl+C):", text);
    } catch (e) {
      if (e?.name === "AbortError") return;
      setError(e?.message || "Could not share or copy the report.");
    }
  };

  return (
    <div className="targets-container" style={{ fontFamily: "Poppins" }}>
      <div className="targets-header">
        <div>
          <Typography variant="h6" className="targets-title">
            Route Tracking
          </Typography>
        </div>

        <div className="targets-filtersRow">
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="route-person-filter-label">Person</InputLabel>
            <Select
              labelId="route-person-filter-label"
              value={employeeId}
              label="Person"
              onChange={(e) => setEmployeeId(e.target.value)}
              disabled={loadingEmployees}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="me">Me</MenuItem>
              {employees.map((emp) => (
                <MenuItem
                  key={emp.id ?? emp.user_id}
                  value={String(emp.user_id ?? emp.id)}
                >
                  {emp.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <DatePicker
            selected={rangeStartDate || rangeEndDate}
            selectsRange
            startDate={rangeStartDate}
            endDate={rangeEndDate}
            onChange={(update) => {
              const [start, end] = update;
              setStartDate(start ? dayjs(start).format("YYYY-MM-DD") : "");
              setEndDate(end ? dayjs(end).format("YYYY-MM-DD") : "");
            }}
            customInput={<DateRangeField />}
            dateFormat="dd-MM-yyyy"
            placeholderText="Select start and end date"
            shouldCloseOnSelect={false}
          />

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="route-village-filter-label">Village</InputLabel>
            <Select
              labelId="route-village-filter-label"
              value={villageFilterId}
              label="Village"
              onChange={(e) => setVillageFilterId(e.target.value)}
              disabled={loadingVillages}
            >
              <MenuItem value="">All</MenuItem>
              {assignedVillages.map((v) => (
                <MenuItem key={v.id} value={v.id}>
                  {v.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            size="small"
            variant="outlined"
            color="inherit"
            onClick={openCreateRouteDialog}
            disabled={savingRoute || loadingVillages}
            sx={{
              minWidth: 140,
              borderColor: "rgba(0,0,0,0.25)",
              color: "rgba(0,0,0,0.8)",
            }}
          >
            Create Route
          </Button>
        </div>
      </div>

      {error && (
        <div className="targets-status">
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        </div>
      )}

      {/* Grid of all route stops for the month */}
      <div className="targets-allGrid">
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: 8,
            gap: 8,
          }}
        >
          <Button
            size="small"
            variant="outlined"
            onClick={() => downloadVisitReportCsv()}
            disabled={gridLoading || csvExportBusy}
          >
            {csvExportBusy ? "Preparing…" : "Download visit report (CSV)"}
          </Button>
        </div>
        {gridLoading ? (
          <div style={{ padding: 16 }}>
            <CircularProgress size={22} />
          </div>
        ) : (
          <DataGridTable
            columns={gridColumns}
            data={gridRows}
            storageKey="routes_grid"
          />
        )}
      </div>

      {route?.id ? (
        <Card sx={{ m: 2, mt: 1, borderRadius: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              Daily route detail report
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Village names, address, visit time, and status for the selected person
              and date (same as the route dialog).
            </Typography>
            {loadingDailyReport ? (
              <CircularProgress size={22} />
            ) : dailyReport ? (
              <>
                <Typography variant="body2" sx={{ mb: 2, fontWeight: 600 }}>
                  {dailyReport.employee_name} · {dailyReport.date} ·{" "}
                  {dailyReport.summary?.visited ?? 0} visited ·{" "}
                  {dailyReport.summary?.not_visited ?? 0} not visited
                  {dailyReport.summary?.skipped
                    ? ` · ${dailyReport.summary.skipped} skipped`
                    : ""}
                </Typography>
                <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>
                  All stops (visit order)
                </Typography>
                {(dailyReport.stops || []).map((s) => (
                  <div key={s.stop_id} style={{ marginBottom: 12 }}>
                    <Typography variant="body2">
                      {s.sequence_no}. {s.display_name} —{" "}
                      {s.status_label || s.status}
                    </Typography>
                    {s.address ? (
                      <Typography variant="caption" display="block" color="text.secondary">
                        Address: {s.address}
                      </Typography>
                    ) : null}
                    {s.status === "visited" &&
                    (s.actual_arrival_time || s.visited_at_display) ? (
                      <Typography variant="caption" display="block" color="text.secondary">
                        Visited at:{" "}
                        {formatDateTimeIST(
                          s.actual_arrival_time || s.visited_at_display
                        ) ||
                          String(
                            s.visited_at_display || s.actual_arrival_time
                          )}
                      </Typography>
                    ) : null}
                    {s.notes ? (
                      <Typography variant="caption" display="block" color="text.secondary">
                        Notes: {s.notes}
                      </Typography>
                    ) : null}
                  </div>
                ))}
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
                  <Button size="small" variant="outlined" onClick={shareDetailReport}>
                    Share report…
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={async () => {
                      const text = buildRouteDetailReportPlainText(dailyReport);
                      try {
                        await navigator.clipboard.writeText(text);
                        window.alert("Copied to clipboard.");
                      } catch {
                        setError("Could not copy to clipboard.");
                      }
                    }}
                  >
                    Copy report
                  </Button>
                </div>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No report for this selection.
              </Typography>
            )}
          </CardContent>
        </Card>
      ) : null}

      <Dialog
        open={plannerOpen}
        onClose={() => setPlannerOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {plannerDateIsPast ? "View route (read-only)" : "Create Route"}
        </DialogTitle>
        <DialogContent dividers>
          {plannerDateIsPast ? (
            <Typography variant="body2" color="error" sx={{ mb: 2 }}>
              This date is in the past. Stops and visit marks cannot be changed.
              You can still share the detail report below from the main page.
            </Typography>
          ) : null}
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="route-person-planner-label">Person</InputLabel>
              <Select
                labelId="route-person-planner-label"
                value={plannerEmployeeId}
                label="Person"
                onChange={(e) => setPlannerEmployeeId(e.target.value)}
                disabled={loadingEmployees}
              >
                <MenuItem value="me">Me</MenuItem>
                {employees.map((emp) => (
                  <MenuItem
                    key={emp.id ?? emp.user_id}
                    value={String(emp.user_id ?? emp.id)}
                  >
                    {emp.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 190 }}>
              <TextField
                size="small"
                label="Date"
                type="date"
                value={plannerDate}
                onChange={(e) => {
                  const nextDate = e.target.value;
                  setPlannerDate(nextDate);
                  if (
                    plannerRepeatUntil &&
                    dayjs(plannerRepeatUntil).isBefore(dayjs(nextDate), 'day')
                  ) {
                    setPlannerRepeatUntil(nextDate);
                  }
                }}
                inputProps={{ max: dayjs().add(365, "day").format("YYYY-MM-DD") }}
                sx={{ minWidth: 190 }}
              />
              <Typography variant="caption" color="text.secondary">
                Weekday: {plannerSelectedWeekday}
              </Typography>
            </div>
            {!plannerDateIsPast ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 220 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={plannerRepeatWeekly}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setPlannerRepeatWeekly(checked);
                        if (!checked) setPlannerRepeatUntil("");
                      }}
                      color="primary"
                    />
                  }
                  label="Repeat weekly on this weekday"
                  sx={{ ml: 0 }}
                />
                {plannerRepeatWeekly ? (
                  <TextField
                    size="small"
                    label="Repeat until"
                    type="date"
                    value={plannerRepeatUntil || plannerDate}
                    onChange={(e) => setPlannerRepeatUntil(e.target.value)}
                    inputProps={{
                      min: plannerDate,
                      max: dayjs(plannerDate).add(365, "day").format("YYYY-MM-DD"),
                    }}
                    sx={{ minWidth: 220 }}
                  />
                ) : null}
              </div>
            ) : null}
          </div>

          <Typography variant="body2" sx={{ marginTop: 2, marginBottom: 1 }}>
            {plannerDateIsPast
              ? "This route is shown for reference only."
              : "Add stops in the order they should be visited. Villages come from Village Master."}
          </Typography>

          {plannerEditStops.map((stop, idx) => {
            const takenVillageIds = new Set();
            plannerEditStops.forEach((s, i) => {
              if (i === idx || s.villageId == null || s.villageId === "")
                return;
              const n = parseInt(String(s.villageId), 10);
              if (!Number.isNaN(n)) takenVillageIds.add(n);
            });
            const villagesForRow = assignedVillages.filter(
              (v) =>
                !takenVillageIds.has(Number(v.id)) ||
                Number(v.id) === Number(stop.villageId)
            );
            return (
            <div
              key={stop.id ?? idx}
              style={{
                display: "flex",
                gap: 8,
                marginBottom: 8,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <FormControl
                size="small"
                sx={{ flex: 1, minWidth: 260 }}
                disabled={loadingVillages || plannerDateIsPast}
              >
                <InputLabel id={`rt-stop-village-label-${idx}`}>
                  {`Stop ${idx + 1} – Village`}
                </InputLabel>
                <Select
                  labelId={`rt-stop-village-label-${idx}`}
                  label={`Stop ${idx + 1} – Village`}
                  value={stop.villageId ?? ""}
                  onChange={(e) => onPlannerStopVillageSelected(idx, e.target.value)}
                >
                  <MenuItem value="">
                    <em>Select village</em>
                  </MenuItem>
                  {villagesForRow.map((v) => (
                    <MenuItem key={v.id} value={String(v.id)}>
                      {v.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="Address (optional)"
                value={stop.address}
                onChange={(e) => handlePlannerStopChange(idx, "address", e.target.value)}
                sx={{ flex: 2, minWidth: 280 }}
                disabled={plannerDateIsPast}
              />
              {stop.id && (
                <FormControl size="small">
                  <div style={{ display: "flex", alignItems: "center", whiteSpace: "nowrap" }}>
                    <Checkbox
                      checked={stop.status === "visited"}
                      onChange={() => {}}
                      color="primary"
                      disabled
                    />
                    <Typography variant="body2">Visited</Typography>
                    {stop.visitedAt ? (
                      <Typography variant="caption" sx={{ ml: 1, color: "text.secondary" }}>
                        {formatDateTimeIST(stop.visitedAt) ||
                          String(stop.visitedAt)}
                      </Typography>
                    ) : null}
                  </div>
                </FormControl>
              )}
            </div>
            );
          })}

          {!plannerDateIsPast ? (
            <Button size="small" onClick={addPlannerEmptyStopRow}>
              Add another stop
            </Button>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlannerOpen(false)} color="inherit">
            Close
          </Button>
          {!plannerDateIsPast ? (
            <Button
              variant="contained"
              onClick={async () => {
                await saveRouteForDay();
                setPlannerOpen(false);
              }}
              disabled={savingRoute}
            >
              {savingRoute ? "Saving..." : "Save Route"}
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>
    </div>
  );
}

